import { useMemo } from 'react';
import { User, UserType } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { useAllCompanies } from 'queries/account';
import { AccountFeature, useAccountFeature } from './use-account-feature';
import { useUserSafe } from './use-user';

export const useIsRestrictedPartnerUser = (): boolean => {
  const profile = useUserSafe();
  const userType = profile?.user?.userType;
  const currentUserCompanyId = profile?.user?.company?.companyId;
  const { data: partnerCompanies } = useAllCompanies();

  return useMemo(() => {
    if (!profile) {
      return false;
    }

    return (
      userType === UserType.Partner &&
      (partnerCompanies ?? []).find(
        ({ companyId }) => companyId === currentUserCompanyId,
      )?.accessLevel?.partnerPermissionSetName ===
        PartnerPermissionName.Restricted
    );
  }, [currentUserCompanyId, partnerCompanies, profile, userType]);
};

export const usePartnerCompanyPermission = (): {
  canPartnerTrackTime: boolean;
} => {
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );
  const profile = useUserSafe();
  const userType = profile?.user?.userType;
  const canTrackTime = profile?.company?.canTrackTime;

  return useMemo(
    () => ({
      canPartnerTrackTime:
        (isTimesheetFeatureEnabled &&
          userType === UserType.Partner &&
          canTrackTime) ??
        false,
    }),
    [canTrackTime, isTimesheetFeatureEnabled, userType],
  );
};

export const isPartnerUser = ({
  userType,
}: Pick<User, 'userType'>): boolean => {
  return userType === UserType.Partner;
};

export const useIsCurrentUserPartner = (): boolean => {
  const profile = useUserSafe();
  const user = profile?.user;

  return useMemo(() => (user ? isPartnerUser(user) : false), [user]);
};

export const useDoesPartnerMatchPermissionName = (
  partnerPermissionName: PartnerPermissionName,
) => {
  const profile = useUserSafe();
  const userType = profile?.user?.userType;
  const currentUserCompanyId = profile?.user?.company?.companyId;
  const { data: partnerCompanies } = useAllCompanies();

  return useMemo(() => {
    if (!profile) {
      return false;
    }

    return (
      userType === UserType.Partner &&
      (partnerCompanies ?? []).find(
        ({ companyId }) => companyId === currentUserCompanyId,
      )?.accessLevel?.partnerPermissionSetName === partnerPermissionName
    );
  }, [
    currentUserCompanyId,
    partnerCompanies,
    partnerPermissionName,
    profile,
    userType,
  ]);
};
